import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChefHat, Plus, Trash2, AlertTriangle, MonitorPlay, Check, Flame, Salad, Martini, Cake, Coffee, Pizza, Sandwich, Utensils, Fish, Soup, GripVertical } from 'lucide-react';
import { Branch, KitchenStation, useBranches, isKdsEnabled } from './BranchesContext';
import { Toggle } from './Toggle';
const iconMap: Record<string, ComponentType<{
  size?: number;
}>> = {
  flame: Flame,
  salad: Salad,
  martini: Martini,
  cake: Cake,
  coffee: Coffee,
  pizza: Pizza,
  sandwich: Sandwich,
  utensils: Utensils,
  fish: Fish,
  soup: Soup
};
const colorClasses: Record<string, {
  bg: string;
  text: string;
  ring: string;
  chipBg: string;
  chipText: string;
}> = {
  orange: {
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    ring: 'ring-orange-200',
    chipBg: 'bg-orange-100',
    chipText: 'text-orange-700'
  },
  emerald: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    ring: 'ring-emerald-200',
    chipBg: 'bg-emerald-100',
    chipText: 'text-emerald-700'
  },
  blue: {
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    ring: 'ring-blue-200',
    chipBg: 'bg-blue-100',
    chipText: 'text-blue-700'
  },
  pink: {
    bg: 'bg-pink-50',
    text: 'text-pink-700',
    ring: 'ring-pink-200',
    chipBg: 'bg-pink-100',
    chipText: 'text-pink-700'
  },
  amber: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    ring: 'ring-amber-200',
    chipBg: 'bg-amber-100',
    chipText: 'text-amber-700'
  },
  purple: {
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    ring: 'ring-purple-200',
    chipBg: 'bg-purple-100',
    chipText: 'text-purple-700'
  },
  rose: {
    bg: 'bg-rose-50',
    text: 'text-rose-700',
    ring: 'ring-rose-200',
    chipBg: 'bg-rose-100',
    chipText: 'text-rose-700'
  },
  indigo: {
    bg: 'bg-indigo-50',
    text: 'text-indigo-700',
    ring: 'ring-indigo-200',
    chipBg: 'bg-indigo-100',
    chipText: 'text-indigo-700'
  }
};
export function StationIconBadge({
  icon,
  color,
  size = 16,
  active = true
}: {
  icon: string;
  color: string;
  size?: number;
  active?: boolean;
}) {
  const Icon = iconMap[icon] || Utensils;
  const c = colorClasses[color] || colorClasses.orange;
  return <div className={`flex items-center justify-center rounded-md ${active ? `${c.chipBg} ${c.chipText}` : 'bg-gray-100 text-gray-400'}`} style={{
    width: size * 1.75,
    height: size * 1.75
  }} data-id="element-820">
      <Icon size={size} data-id="element-821" />
    </div>;
}
export function BranchStationsSection({
  branch
}: {
  branch: Branch;
}) {
  const {
    updateBranch,
    addStation,
    updateStation,
    removeStation
  } = useBranches();
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newStation, setNewStation] = useState<Partial<KitchenStation>>({
    name: '',
    color: 'orange',
    active: true
  });
  const kdsEnabled = isKdsEnabled(branch);
  const handleAdd = () => {
    const id = addStation(branch.id, {
      name: 'New station',
      icon: 'utensils'
    });
    setEditingId(id);
  };
  return <div className="space-y-6" data-id="element-822">
      <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-id="element-823">
        <div className="flex items-start justify-between gap-4" data-id="element-824">
          <div data-id="element-825">
            <h3 className="text-sm font-bold text-gray-900 mb-1 flex items-center gap-2" data-id="element-826">
              <MonitorPlay size={16} className="text-orange-500" data-id="element-827" />
              Kitchen Display System (KDS)
            </h3>
            <p className="text-xs text-gray-500" data-id="element-828">
              {kdsEnabled ? 'Orders flow through prep stations and the kitchen marks each item ready. Best for full-service restaurants with multiple stations.' : 'Counter service mode. Orders skip the prep step and complete in a single tap — best for cafes, food trucks, or counter-only spots.'}
            </p>
          </div>
          <Toggle checked={kdsEnabled} onChange={v => updateBranch(branch.id, {
          kdsEnabled: v
        })} data-id="element-829" />
        </div>
      </div>

      {!kdsEnabled && <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-id="element-830">
          <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-831" />
          <p className="text-sm text-amber-800" data-id="element-832">
            <span className="font-bold" data-id="element-833">
              Stations are unused in Counter Service mode.
            </span>
            <br data-id="element-834" />
            They'll be remembered if you turn KDS back on.
          </p>
        </div>}

      <div className={`transition-opacity duration-200 ${!kdsEnabled ? 'opacity-60 pointer-events-none' : ''}`} data-id="element-835">
        {branch.stations.length === 0 ? <div className="text-center py-10 px-4 rounded-xl border border-dashed border-gray-300 bg-white" data-id="element-836">
            <ChefHat size={20} className="mx-auto text-gray-400 mb-2" strokeWidth={1.5} data-id="element-837" />
            <div className="text-sm font-semibold text-gray-700" data-id="element-838">
              No prep stations yet
            </div>
            <div className="text-xs text-gray-500 mt-0.5 max-w-xs mx-auto" data-id="element-839">
              Add stations like Hot Kitchen, Bar, or Cold/Salad so the kitchen
              display can route items correctly.
            </div>
          </div> : <div className="space-y-2" data-id="element-840">
            {branch.stations.map(station => <StationRow key={station.id} station={station} isEditing={editingId === station.id} onStartEdit={() => setEditingId(station.id)} onDone={() => setEditingId(null)} onUpdate={patch => updateStation(branch.id, station.id, patch)} onRemove={() => removeStation(branch.id, station.id)} data-id="element-841" />)}
          </div>}

        <button onClick={handleAdd} className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border border-dashed border-gray-300 text-sm font-semibold text-gray-600 hover:border-orange-300 hover:text-orange-600 hover:bg-orange-50/40 transition-colors" data-id="element-842">
          <Plus size={15} data-id="element-843" />
          Add prep station
        </button>

        {branch.stations.length > 1 && <div className="mt-4 p-4 rounded-xl bg-blue-50 border border-blue-100 flex items-start gap-3" data-id="element-844">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 text-blue-600" data-id="element-845">
              <ChefHat size={16} data-id="element-846" />
            </div>
            <p className="text-xs text-blue-800 leading-relaxed" data-id="element-847">
              <span className="font-bold" data-id="element-848">Multi-station routing active.</span>{' '}
              Items route to one of these stations based on category. The
              kitchen display can be filtered per station, or shown as an
              expediter overview across all stations.
            </p>
          </div>}
      </div>
    </div>;
}
function StationRow({
  station,
  isEditing,
  onStartEdit,
  onDone,
  onUpdate,
  onRemove
}: {
  station: KitchenStation;
  isEditing: boolean;
  onStartEdit: () => void;
  onDone: () => void;
  onUpdate: (patch: Partial<KitchenStation>) => void;
  onRemove: () => void;
}) {
  const c = colorClasses[station.color] || colorClasses.orange;
  return <motion.div layout className={`rounded-xl border bg-white transition-colors ${station.active ? 'border-gray-200' : 'border-gray-200 opacity-70'}`} data-id="element-849">
      {/* Collapsed row */}
      <div className="flex items-center gap-3 p-3" data-id="element-850">
        <GripVertical size={14} className="text-gray-300 cursor-grab flex-shrink-0" data-id="element-851" />
        <StationIconBadge icon={station.icon} color={station.color} active={station.active} data-id="element-852" />
        <div className="flex-1 min-w-0" data-id="element-853">
          {isEditing ? <input type="text" value={station.name} onChange={e => onUpdate({
          name: e.target.value
        })} onBlur={onDone} onKeyDown={e => {
          if (e.key === 'Enter' || e.key === 'Escape') {
            ;
            (e.target as HTMLInputElement).blur();
          }
        }} autoFocus className="w-full px-2 py-1 rounded-md border border-orange-300 bg-white text-sm font-bold text-gray-900 focus:outline-none focus:ring-2 focus:ring-orange-100" data-id="element-854" /> : <button onClick={onStartEdit} className="text-left w-full" data-id="element-855">
              <div className="font-bold text-sm text-gray-900" data-id="element-856">
                {station.name}
              </div>
              <div className="text-[11px] text-gray-500" data-id="element-857">
                ID:{' '}
                <span className="font-mono text-gray-600" data-id="element-858">{station.id}</span>
                {!station.active && <span className="ml-2 text-gray-400" data-id="element-859">· Inactive</span>}
              </div>
            </button>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0" data-id="element-860">
          <Toggle checked={station.active} onChange={v => onUpdate({
          active: v
        })} data-id="element-861" />
          <button onClick={onRemove} className="p-1.5 rounded-md text-gray-400 hover:text-rose-600 hover:bg-rose-50 transition-colors" title="Remove station" data-id="element-862">
            <Trash2 size={14} data-id="element-863" />
          </button>
        </div>
      </div>

      {/* Expanded editor */}
      <AnimatePresence initial={false} data-id="element-864">
        {isEditing && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="overflow-hidden" data-id="element-865">
            <div className="px-3 pb-3 pt-1 border-t border-gray-100" data-id="element-866">
              <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5 mt-2" data-id="element-867">
                Icon
              </div>
              <div className="flex flex-wrap gap-1.5" data-id="element-868">
                {stationIcons.map(ic => {
              const Icon = iconMap[ic] || Utensils;
              const active = station.icon === ic;
              return <button key={ic} onClick={() => onUpdate({
                icon: ic
              })} className={`w-8 h-8 rounded-md flex items-center justify-center transition-all ${active ? `${c.chipBg} ${c.chipText} ring-2 ${c.ring}` : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`} data-id="element-869">
                      <Icon size={14} data-id="element-870" />
                    </button>;
            })}
              </div>
              <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5 mt-3" data-id="element-871">
                Color
              </div>
              <div className="flex flex-wrap gap-1.5" data-id="element-872">
                {stationColors.map(col => {
              const cc = colorClasses[col] || colorClasses.orange;
              const active = station.color === col;
              return <button key={col} onClick={() => onUpdate({
                color: col
              })} className={`w-7 h-7 rounded-md flex items-center justify-center transition-all ${cc.chipBg} ${active ? `ring-2 ${cc.ring}` : 'hover:opacity-80'}`} data-id="element-873">
                      {active && <Check size={12} className={cc.chipText} strokeWidth={3} data-id="element-874" />}
                    </button>;
            })}
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>;
}